import { useButton } from '@nautilusbio/nb-design-system';
import { Link } from '@tanstack/react-router';
import React, { type FC, memo } from 'react';

export interface NameLinkCellProps {
  id: string | number;
  name: string;
  /** A registered app route path, e.g. "/studies/$studyId" */
  to: string;
  /** Route params, e.g. { studyId: '123' } */
  params: Record<string, string>;
  /** Optional `from` route for relative link resolution */
  from?: string;
  /** data-testid attribute */
  testId?: string;
  /** Extra className merged into the button style */
  className?: string;
  /** Whether the cell should fill full height (default: false) */
  fullHeight?: boolean;
  /** Button size passed to useButton (default: 'sm') */
  size?: 'xs' | 'sm';
  /** aria-label for the link */
  ariaLabel?: string;
}

/**
 * Shared AG Grid cell renderer that renders an entity name as a
 * full-width unstyled Link button navigating to a detail route.
 *
 * Use page-local thin wrappers to adapt AG Grid's `{ data }` prop
 * to this component's explicit props.
 */
const NameLinkCell: FC<NameLinkCellProps> = ({
  id,
  name,
  to,
  params,
  from,
  testId,
  className,
  fullHeight = false,
  size = 'sm',
  ariaLabel,
}) => {
  const { buttonClassName } = useButton({
    className,
    fullHeight,
    fullWidth: true,
    size,
    visual: 'unstyled',
  });

  return (
    <Link
      aria-label={ariaLabel}
      className={buttonClassName}
      data-testid={testId ?? `name-link-${id}`}
      // TanStack Router's to/from/params types are tightly coupled to router
      // generics and cannot be cleanly extracted for a generic wrapper component.
      // Type safety is enforced at the call sites (OrgNameCell, StudyNameCell)
      // where concrete route strings are validated against the registered routes.
      from={from as never}
      params={params as never}
      preload="intent"
      to={to as never}
    >
      {name}
    </Link>
  );
};

export default memo(NameLinkCell);
